import { useState, useEffect } from "react";

/**
 * Custom hook that detects whether the viewport is at or below a given
 * breakpoint width. Uses `window.matchMedia` for the initial check and
 * subscribes to the `change` event for reactive updates when the viewport
 * crosses the threshold.
 *
 * Falls back to `false` (desktop) when `matchMedia` is unavailable (SSR).
 */
export function useIsMobile(breakpoint: number = 768): boolean {
  const [isMobile, setIsMobile] = useState<boolean>(() => {
    try {
      return window.matchMedia(`(max-width: ${breakpoint}px)`).matches;
    } catch {
      return false;
    }
  });

  useEffect(() => {
    let mql: MediaQueryList;
    try {
      mql = window.matchMedia(`(max-width: ${breakpoint}px)`);
    } catch {
      return;
    }

    // Sync in case the value changed between the initial useState and this effect
    setIsMobile(mql.matches);

    const handler = (e: MediaQueryListEvent) => {
      setIsMobile(e.matches);
    };

    mql.addEventListener("change", handler);
    return () => {
      mql.removeEventListener("change", handler);
    };
  }, [breakpoint]);

  return isMobile;
}
